import React, { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import { HVSubstationDropdown } from "../HVSubstationDropdown";
import type { WorkflowReducer, WorkflowType } from "./workflowReducer";
import { Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { CompanyDropdown } from "../CompanyDropdown";
import { SiteDropdown } from "../SiteDropdown";
import { useCompaniesByCustomerId } from "../data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../data/useSitesByCompanyId";
import { ConfirmationDialog } from "../shared/ConfirmationDialog";
import { SwitchgearDropdown } from "../SwitchgearDropdown";

interface Props {
  workflowReducer: WorkflowReducer;
  customerId: number;
  selectedSubstationId?: number;
  editMode?: boolean;
  companyName?: string;
  siteName?: string;
  onCompanyNameChange: (companyName: string | undefined) => void;
  onSiteNameChange: (siteName: string | undefined) => void;
}

const useStyles = makeStyles((theme) => ({
  dropdown: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    textAlign: "start",
  },
  gridContainer: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  label: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
}));

export function WorkflowSubstationSelector(props: Props) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
    clearErrors,
    unregister,
  } = useFormContext();

  const isWorkflowV1 = props.workflowReducer.state.workflow.workflowDetail.version === 1;

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const classes = useStyles();
  const companies = useCompaniesByCustomerId(props.customerId);

  const companyId = useMemo(() => {
    if (companies && props.companyName) {
      return companies.data?.find((c) => c.name === props.companyName)?.id;
    }
  }, [companies, props.companyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && props.siteName) {
      return sites.data?.find((c) => c.name === props.siteName)?.id;
    }
  }, [sites.data, props.siteName]);

  const handleCompanyChange = useCallback(
    (selectedCompanyName: string) => {
      props.onCompanyNameChange(selectedCompanyName);
      props.onSiteNameChange(undefined);
      props.workflowReducer.updateWorkflowDetails({
        type: "clearSubstationSwitchgear",
      });

      setValue("substationTab.substation_id", undefined, {
        shouldValidate: false,
      });
      setValue("substationTab.switchgear_id", undefined, {
        shouldValidate: false,
      });
      clearErrors(["substationTab.substation_id", "substationTab.switchgear_id"]);
    },
    [setValue, clearErrors, props],
  );

  const handleSiteChange = useCallback(
    (selectedSiteName: string) => {
      props.onSiteNameChange(selectedSiteName);
      props.workflowReducer.updateWorkflowDetails({
        type: "clearSubstationSwitchgear",
      });

      setValue("substationTab.substation_id", undefined, {
        shouldValidate: false,
      });
      setValue("substationTab.switchgear_id", undefined, {
        shouldValidate: false,
      });
      clearErrors(["substationTab.substation_id", "substationTab.switchgear_id"]);
    },
    [setValue, clearErrors, props],
  );

  function handleTypeRadioButtonClick(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedValue = (event.target as HTMLInputElement).value as WorkflowType;
    if (
      selectedValue === "generic" &&
      props.workflowReducer.state.groupedMeasurements.some((m) => m.panels.length > 0)
    ) {
      props.workflowReducer.state.groupedMeasurements.forEach((m) => {
        unregister(`measurementsTab.${m.groupId}.panelCount`);
      });

      setShowConfirmationDialog(true);
    } else {
      if (selectedValue === "generic") {
        unregister(["substationTab.substation_id", "substationTab.switchgear_id"]);
      }

      props.workflowReducer.updateWorkflowDetails({
        type: "updateWorkflowType",
        workflowType: selectedValue,
      });
    }
  }

  function handleConfirmGenericTypeChange() {
    props.workflowReducer.updateWorkflowDetails({
      type: "updateWorkflowType",
      workflowType: "generic",
    });
    clearErrors(["substationTab.substation_id", "substationTab.switchgear_id"]);
  }
  function handleSubstationChange(selectedSubstationId?: number, selectedSubstationName?: string) {
    props.workflowReducer.updateWorkflowDetails({
      type: "updateSubstationId",
      substationId: selectedSubstationId,
      substationName: selectedSubstationName,
    });

    setValue("substationTab.substation_id", selectedSubstationId, {
      shouldValidate: true,
    });
    if (selectedSubstationId === undefined) handleSwitchgearChange(undefined);

    clearErrors("substationTab.switchgear_id");
  }
  function handleSwitchgearChange(
    selectedSwitchgearId?: number,
    selectedSwitchgearName?: string,
    _index?: number,
    assetType?: string,
  ) {
    props.workflowReducer.updateWorkflowDetails({
      type: "updateSwitchgearId",
      switchgearId: selectedSwitchgearId,
      switchgearName: selectedSwitchgearName,
      assetType: assetType,
    });
    setValue("substationTab.switchgear_id", selectedSwitchgearId, {
      shouldValidate: true,
    });
  }

  function closeConfirmationDialog() {
    setShowConfirmationDialog(false);
  }

  return (
    <React.Fragment>
      <List dense>
        <ListSubheader disableGutters>{t("workflows.workflowApplication")}</ListSubheader>

        {props.editMode && (
          <RadioGroup value={props.workflowReducer.state.workflowType} onChange={handleTypeRadioButtonClick}>
            <ListItem>
              <FormControlLabel
                value="generic"
                control={<Radio color="primary" />}
                label={t("substations.anySubstation")}
              />
            </ListItem>
            <ListItem>
              <FormControlLabel
                value="substation"
                control={<Radio color="primary" />}
                label={t("substations.specificSubstation")}
              />
            </ListItem>
          </RadioGroup>
        )}
        <ListItem>
          {props.workflowReducer.state.workflowType === "substation" && (
            <React.Fragment>
              <ListItemText
                inset
                primary={
                  <>
                    {props.editMode && (
                      <Grid container spacing={2} className={classes.gridContainer}>
                        <Grid item xs={2} sm={2} md={2}>
                          <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
                            {t("common.company")}:
                          </Typography>
                        </Grid>
                        <Grid item xs={10} sm={10} md={10}>
                          <CompanyDropdown
                            customerId={props.customerId}
                            selectedCompanyName={props.companyName}
                            onCompanyChange={handleCompanyChange}
                            clearAuto
                            className={classes.dropdown}
                            width="300px"
                          />
                        </Grid>
                      </Grid>
                    )}
                    {props.editMode && (
                      <Grid container spacing={2} className={classes.gridContainer}>
                        <Grid item xs={2} sm={2} md={2}>
                          <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
                            {t("common.site")}:
                          </Typography>
                        </Grid>
                        <Grid item xs={10} sm={10} md={10}>
                          <SiteDropdown
                            companyId={companyId}
                            customerId={props.customerId}
                            selectedSiteName={props.siteName}
                            onSiteChange={handleSiteChange}
                            clearAuto
                            className={classes.dropdown}
                            width="300px"
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid container spacing={2} className={classes.gridContainer}>
                      <Grid item xs={2} sm={2} md={2}>
                        <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
                          {t("substations.substation")}:
                        </Typography>
                      </Grid>
                      <Grid item xs={10} sm={10} md={10}>
                        {props.editMode ? (
                          <HVSubstationDropdown
                            selectedSubstationId={props.selectedSubstationId}
                            key="dropdown-assetId"
                            customerId={props.customerId}
                            companyId={companyId}
                            siteId={siteId}
                            onSubstationChange={handleSubstationChange}
                            createable
                            invalid={errors?.substationTab?.substation_id}
                            invalidMessage={t("workflows.substationRequiredIfSubstationSpecific")}
                            {...register("substationTab.substation_id", {
                              required: props.workflowReducer.state.workflowType === "substation",
                              value: props.selectedSubstationId,
                            })}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {props.workflowReducer.state.workflow?.substationName}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    {isWorkflowV1 && (
                      <Grid container spacing={2} className={classes.gridContainer}>
                        <Grid item xs={2} sm={2} md={2}>
                          <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
                            {t("switchgear.switchgear")}:
                          </Typography>
                        </Grid>
                        <Grid item xs={10} sm={10} md={10}>
                          {props.editMode ? (
                            <SwitchgearDropdown
                              substationId={props.selectedSubstationId}
                              selectedSwitchgearId={props.workflowReducer.state.workflow?.workflowDetail.switchgear_id}
                              onSwitchgearChange={handleSwitchgearChange}
                              createable
                              invalid={errors?.substationTab?.switchgear_id}
                              invalidMessage={t("workflows.switchgearRequiredIfSubstationSpecific")}
                              customerId={props.customerId}
                              {...register("substationTab.switchgear_id", {
                                required: props.workflowReducer.state.workflowType === "substation",
                                value: props.workflowReducer.state.workflow?.workflowDetail.switchgear_id,
                              })}
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {props.workflowReducer.state.workflow?.switchGearName}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </>
                }
              />
            </React.Fragment>
          )}
          {(props.workflowReducer.state.workflowType === "generic" || !props.workflowReducer.state.workflowType) &&
            !props.editMode && (
              <React.Fragment>
                <ListItemText
                  inset
                  style={{ border: "0px solid red" }}
                  primary={<Typography variant="body2">{t("substations.allSubstations")}</Typography>}
                />
              </React.Fragment>
            )}
        </ListItem>
      </List>

      {showConfirmationDialog && (
        <ConfirmationDialog
          message={t("workflows.confirmChangeToGenericType", {
            selectedSwitchgear: props.workflowReducer.state.workflow?.switchGearName,
          })}
          onAccept={handleConfirmGenericTypeChange}
          onClose={closeConfirmationDialog}
          visible={showConfirmationDialog}
          header={t("workflows.changeToGenericConfirmation")}
          closeMessage={t("common.cancel")}
          icon="warning"
        />
      )}
    </React.Fragment>
  );
}
